// useAiChatStream — hela stream-loopen för AI-assistenten: endpoint-
// gating (S-024), system-prompt-komposition, SSE-parsning, moderation
// (exfil-guard, stream-safety, [SCOPE_VIOLATION]-strikes), tool-call-
// ackumulering, layout-validator-retry (A-113), model-fallback (E-009)
// och conversation-persistering. Extraherad VERBATIM ur
// AiAssistantDialog (#1 i PRE_RELEASE_REVIEW_100, slutskivan).
//
// Hooken äger: chatAbortRef (abort vid unmount + Stop-knappen),
// layoutRetryCountRef (+ max-retry-konstant), sendMessagesRef
// (själv-referens för layout-retry-omsändning).

import { useCallback, useRef, useEffect, type Dispatch, type SetStateAction, type MutableRefObject } from 'react';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { resolveAiAuthToken } from '@/lib/aiAuthToken';
import { resolveEmittedModelName } from '@/lib/aiModelName';
import { isAllowedAiEndpoint, explainAiRejection } from '@/lib/aiEndpointAllowlist';
import { ANTI_EXFILTRATION_BLOCK } from '@/lib/adminAiSystemPrompt';
import { isStreamSafe, LEAK_REFUSAL_MESSAGE } from '@/lib/aiResponseFilter';
import { FLOW_WEAVER_SYSTEM_PROMPT } from '@/lib/aiAssistantPrompt';
import {
  type Msg,
  extractJsonBlocks,
  stripJsonBlocks,
  injectToolResultAsMessage,
} from '@/lib/aiMessageHelpers';
import {
  parseModelNotAllowedResponse,
  pickFallbackModel,
  fallbackToastMessage,
} from '@/lib/aiModelFallback';
import {
  createToolCallAccumulator,
  applyToolCallChunks,
  flushAccumulator,
} from '@/lib/aiToolCallStream';
import { validateLayout, formatViolationsForAi } from '@/lib/layoutValidator';
import { parseWorkflowState } from '@/lib/workflowSchema';
import { isCompactFormat, expandCompactWorkflow, normaliseLeanFormat } from '@/lib/compactWorkflow';
import { markLegacyBanned, incrementLegacyStrike, LEGACY_MAX_STRIKES } from '@/lib/legacyStrikesStorage';
import { aiOps } from '@/lib/aiOpsLog';
import { dispatchProcessMotorTool } from '@/lib/processMotorTools';
import { recordModerationEvent } from '@/hooks/useModerationStrikes';
import type { Intent } from '@/lib/intentTypes';
import type { WorkflowState, MultiSheetWorkflowState } from '@/types/workflow';
import type { AiConfig } from '@/hooks/useAiEndpointConfig';
import type { useModerationStrikes } from '@/hooks/useModerationStrikes';

const VIOLATION_TAG = '[SCOPE_VIOLATION]';

interface UseAiChatStreamArgs {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setMessages: Dispatch<SetStateAction<Msg[]>>;
  config: AiConfig;
  getChatUrl: () => string;
  persistConfig: (next: AiConfig) => void;
  moderationV2: boolean;
  dbStrikes: ReturnType<typeof useModerationStrikes>;
  appendMessage: (args: {
    conversationId: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    [key: string]: unknown;
  }) => Promise<unknown>;
  conversationIdRef: MutableRefObject<string | null>;
  validateLayoutEnabled: boolean;
  aiHoningPromptRef: MutableRefObject<string | undefined>;
  setActiveFlag: Dispatch<SetStateAction<{
    ruleId: string;
    severity: 'advisory' | 'violation';
    humanReadable: string;
    correlationId: string;
    eventId?: string;
  } | null>>;
  setPendingToolCall: Dispatch<SetStateAction<{
    id: string;
    name: 'trigger_workflow_run' | 'list_recent_workflow_runs';
    args: Record<string, unknown>;
  } | null>>;
  setLegacyStrikes: Dispatch<SetStateAction<number>>;
  setLegacyBanned: Dispatch<SetStateAction<boolean>>;
}

export function useAiChatStream({
  setIsLoading,
  setMessages,
  config,
  getChatUrl,
  persistConfig,
  moderationV2,
  dbStrikes,
  appendMessage,
  conversationIdRef,
  validateLayoutEnabled,
  aiHoningPromptRef,
  setActiveFlag,
  setPendingToolCall,
  setLegacyStrikes,
  setLegacyBanned,
}: UseAiChatStreamArgs) {
  const chatAbortRef = useRef<AbortController | null>(null);
  // A-113: layout-validator-retry-räknare; nollställs per user-send.
  const layoutRetryCountRef = useRef(0);
  const LAYOUT_MAX_RETRIES = 2;
  // Själv-referens: layout-retryn skickar om konversationen via ref så
  // sendMessages slipper sig själv i deps (stale-closure-safe).
  const sendMessagesRef = useRef<(m: Msg[]) => Promise<void>>(async () => {});

  useEffect(() => {
    return () => {
      chatAbortRef.current?.abort();
    };
  }, []);

  const sendMessages = useCallback(async (
    allMessages: Msg[],
    extendContext?: { intent: Intent; previous_version_content: MultiSheetWorkflowState },
  ) => {
    setIsLoading(true);
    let assistantSoFar = '';
    const chatUrl = getChatUrl();
    const isCustomEndpoint = !!config.endpoint;
    let ourController: AbortController | null = null;

    // S-024: gate custom endpoints mot allowlist innan POST. Default-pathen
    // (Supabase edge-fn) sker utan check eftersom URL:en är statisk +
    // auth:ad. Custom endpoint måste matcha en betrodd host — annars
    // refuse:a att skicka conversation + persona + workflow-state dit.
    if (isCustomEndpoint && !isAllowedAiEndpoint(chatUrl, {
      supabaseHost: import.meta.env.VITE_SUPABASE_URL,
      allowLocalhost: import.meta.env.DEV,
    })) {
      const reason = explainAiRejection(chatUrl);
      toast({ title: 'Endpoint blockerad', description: reason, variant: 'destructive' });
      setIsLoading(false);
      setMessages(prev => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
          updated[lastIdx] = { ...updated[lastIdx], error: reason };
        } else {
          updated.push({ role: 'assistant', content: '', error: reason });
        }
        return updated;
      });
      return;
    }

    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };

      let body: string;

      // Prepend the FlowWeaver system prompt so the AI is deeply knowledgeable
      // If a workflow profile honing prompt exists, append it for domain specialisation
      const honing = aiHoningPromptRef.current;
      const fullSystemPrompt = honing
        ? `${FLOW_WEAVER_SYSTEM_PROMPT}\n\n═══════════════════════════════════════\n  WORKFLOW SPECIALISATION\n═══════════════════════════════════════\n${honing}\n\n${ANTI_EXFILTRATION_BLOCK}`
        : `${FLOW_WEAVER_SYSTEM_PROMPT}\n\n${ANTI_EXFILTRATION_BLOCK}`;
      const messagesWithSystem: Array<{ role: string; content: string }> = [
        { role: 'system', content: fullSystemPrompt },
        ...allMessages,
      ];

      if (isCustomEndpoint) {
        // OpenAI-compatible API format
        headers['Authorization'] = `Bearer ${config.apiKey}`;
        const payload: Record<string, unknown> = {
          messages: messagesWithSystem,
          stream: true,
        };
        if (config.model) {
          payload.model = config.model;
        }
        // OpenAI-kompatibel endpoint accepterar inte extra fält officiellt,
        // men Lovable Gateway och vår egen edge läser dem som passthrough —
        // skada gör inte att skicka med dem här också.
        if (extendContext) {
          payload.intent = extendContext.intent;
          payload.previous_version_content = extendContext.previous_version_content;
        }
        body = JSON.stringify(payload);
      } else {
        // Legacy Supabase endpoint (ai-workflow-assistant). Edge-funktionen
        // förväntar sig { messages, intent?, previous_version_content? } —
        // intent+snapshot aktiverar EXTEND-guard-prompten (Batch DDD).
        //
        // S-010: prefer user-session-token så edge får user-claims för
        // tier-gate (I-004), per-user-rate-limit och user_id-loggning i
        // ai_ops_event. Anon-key är fallback för icke-inloggade users —
        // edge accepterar båda, men med user-JWT kan den differentiera.
        const { data: sessionData } = await supabase.auth.getSession();
        const authToken = resolveAiAuthToken(
          sessionData.session?.access_token,
          import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        );
        headers['Authorization'] = `Bearer ${authToken}`;
        const payload: Record<string, unknown> = { messages: messagesWithSystem };
        if (extendContext) {
          payload.intent = extendContext.intent;
          payload.previous_version_content = extendContext.previous_version_content;
        }
        body = JSON.stringify(payload);
      }

      chatAbortRef.current?.abort();
      const controller = new AbortController();
      chatAbortRef.current = controller;
      // Keep a local reference: the global `chatAbortRef` may be
      // replaced by a *next* send before this call's finally runs. We
      // use `ourController` in the finally to make sure we only reset
      // `isLoading` for our own in-flight request.
      ourController = controller;

      const resp = await fetch(chatUrl, { method: 'POST', headers, body, signal: controller.signal });

      // S-141: plocka X-Correlation-ID från response-header (edge-fn sätter
      // den via `withCorrelationHeaders` + `Access-Control-Expose-Headers`).
      // Inkluderas i klient-loggar nedan så cross-stack-incident-utredning
      // (admin_ai_correlation_replay-RPC) kan länka user-AI-warnings till
      // ai_ops_event-rader. Symmetri med S-138 i useAdminAIChat. Header-namnet
      // är case-insensitive enligt Fetch spec så `get('x-correlation-id')`
      // matchar oavsett case.
      const correlationId = resp.headers.get('x-correlation-id') ?? 'unknown';

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: 'Unknown error' }));
        // A-010 closeout: emitt modelGateBlocked när edge-fn:en returnerar
        // 402 model-not-allowed så aiOps-dashboard visar tier-friction.
        if (resp.status === 402 && err?.error === 'model-not-allowed') {
          void aiOps.modelGateBlocked({
            requestedModel: String(err.requested ?? ''),
            userTier: String(err.tier ?? 'free'),
            conversationId: conversationIdRef.current ?? undefined,
          });
          // E-009: auto-fallback — istället för att kasta, pick:a närmaste
          // allowed-modell och visa toast. Caller-retry:n sköts av
          // sendMessagesRef så strömningen kan restartas från scratch.
          const parsed = parseModelNotAllowedResponse(err);
          if (parsed) {
            const fallback = pickFallbackModel(parsed);
            if (fallback) {
              toast({
                title: 'Modell-byte',
                description: fallbackToastMessage(parsed.requested, fallback),
              });
              // Uppdatera config så nästa send använder fallback-modellen.
              // persistConfig skriver till localStorage + re-render:ar
              // sendMessages-closure så nästa fetch picker den nya model:n.
              persistConfig({ ...config, model: fallback });
              // INTE kasta error — så UI inte visar error-bubble. Men vi
              // avbryter den här streamen; user kan klicka Send igen för
              // att fortsätta med fallback-modellen.
              setIsLoading(false);
              return;
            }
          }
        }
        throw new Error(err.error?.message || err.error || `Error ${resp.status}`);
      }

      if (!resp.body) throw new Error('No stream body');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = '';
      let streamDone = false;
      // S-127: stream-time leak-gate. Tidigare hade workflow-AI:n inget
      // post-stream-filter alls — läck-text:er render:ades i UI direkt.
      // `isChunkSafe` kontrollerar varje delta innan append; vid match
      // sätts hela assistant-meddelandet till LEAK_REFUSAL_MESSAGE,
      // reader cancelas och vi bryter ut ur stream-loop:en.
      let chunkLeakDetected = false;
      // I-005 B2: accumulate tool-call-chunks från SSE-delta via
      // pure lib (src/lib/aiToolCallStream).
      const toolCallAccumulator = createToolCallAccumulator();

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') { streamDone = true; break; }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              // S-127 + S-129: leak-gate per delta med cross-chunk-matchning.
              // `isStreamSafe` matchar LEAK_PATTERNS mot sliding-window
              // (sista 200 chars av `assistantSoFar` + nya delta:n) — fångar
              // fraser som spänner över flera chunks som `isChunkSafe`
              // missade.
              if (!isStreamSafe(assistantSoFar, content)) {
                chunkLeakDetected = true;
                streamDone = true;
                assistantSoFar = LEAK_REFUSAL_MESSAGE;
                setMessages(prev => {
                  const last = prev[prev.length - 1];
                  if (last?.role === 'assistant') {
                    return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: LEAK_REFUSAL_MESSAGE } : m);
                  }
                  return [...prev, { role: 'assistant', content: LEAK_REFUSAL_MESSAGE }];
                });
                console.warn('[ai-assistant] leak detected mid-stream, response replaced', { cid: correlationId });
                try { await reader.cancel(); } catch { /* already closed */ }
                break;
              }
              assistantSoFar += content;
              const snapshot = assistantSoFar;
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant') {
                  return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: snapshot } : m);
                }
                return [...prev, { role: 'assistant', content: snapshot }];
              });
            }
            // I-005 B2: delegate chunk-accumulation till pure lib
            const toolCallChunks = parsed.choices?.[0]?.delta?.tool_calls;
            if (toolCallChunks) {
              applyToolCallChunks(toolCallAccumulator, toolCallChunks);
            }
          } catch {
            textBuffer = line + '\n' + textBuffer;
            break;
          }
        }
      }
      if (chunkLeakDetected) {
        setIsLoading(false);
        return;
      }

      // Flush remaining
      if (textBuffer.trim()) {
        for (let raw of textBuffer.split('\n')) {
          if (!raw) continue;
          if (raw.endsWith('\r')) raw = raw.slice(0, -1);
          if (raw.startsWith(':') || raw.trim() === '') continue;
          if (!raw.startsWith('data: ')) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === '[DONE]') continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantSoFar += content;
              const snapshot = assistantSoFar;
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant') {
                  return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: snapshot } : m);
                }
                return [...prev, { role: 'assistant', content: snapshot }];
              });
            }
          } catch { /* ignore */ }
        }
      }

      // If no streaming content was received, try to parse as a non-streaming response
      if (!assistantSoFar) {
        // The response might have been non-streaming JSON
        toast({ title: 'Note', description: 'Response received but no streaming content detected.' });
      }

      // A-010 (subset): fyll ModelUsageWidget i AiOpsPanel — emit:a
      // `model_used`-event efter lyckad stream. Model är `config.model`
      // (custom endpoint) eller den hårdkodade default-modellen i
      // `ai-workflow-assistant`-edge-funktionen. Fire-and-forget; faller
      // silent om user inte är inloggad (emit-helpern RPC:ar mot DB).
      if (assistantSoFar.length > 0) {
        void aiOps.modelUsed({
          model: resolveEmittedModelName(isCustomEndpoint, config.model),
          conversationId: conversationIdRef.current ?? undefined,
        });
      }

      // I-005 B3/B4: process accumulated tool-calls via pure lib.
      // Read-ops kör direkt; write-ops sätter pendingToolCall →
      // TriggerApprovalDialog. Tool-result injekteras som assistant-
      // message så AI:n ser resultatet i nästa turn.
      const completedCalls = flushAccumulator(toolCallAccumulator);
      for (const completed of completedCalls) {
        if (completed.needsApproval) {
          setPendingToolCall({
            id: completed.id,
            name: completed.call.name,
            args: completed.call.args,
          });
        } else {
          const result = await dispatchProcessMotorTool(completed.call);
          injectToolResultAsMessage(setMessages, completed.call.name, result);
        }
      }

      // Detect scope violations in the response
      if (assistantSoFar.includes(VIOLATION_TAG)) {
        if (moderationV2) {
          // v2: record event i DB; useModerationStrikes-hook invalideras
          // → strike-count + ban-status läses live från RPC.
          const rec = await recordModerationEvent({
            ruleId: 'safety.scope_violation',
            ruleVersion: 1,
            severity: 'violation',
            humanReadable:
              'AI-svaret markerades som utanför scope (prompt försökte kringgå ' +
              'systemets avgränsning för workflow-design).',
          });
          dbStrikes.refresh();
          if (rec.success && rec.eventId) {
            setActiveFlag({
              ruleId: 'safety.scope_violation',
              severity: 'violation',
              humanReadable:
                'AI-svaret markerades som utanför scope. Klassificeraren bedömde ' +
                'att prompten försökte få AI:n att bryta mot sina begränsningar.',
              correlationId: rec.correlationId ?? 'unknown',
              eventId: rec.eventId,
            });
          }
        } else {
          // Legacy path
          const newStrikes = incrementLegacyStrike();
          setLegacyStrikes(newStrikes);
          if (newStrikes >= LEGACY_MAX_STRIKES) {
            markLegacyBanned();
            setLegacyBanned(true);
            setMessages((prev) => {
              const updated = [...prev];
              const lastIdx = updated.length - 1;
              if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                updated[lastIdx] = {
                  role: 'assistant',
                  content: 'AI-assistenten är tillfälligt blockerad pga upprepade scope-flaggningar. Om du tror att detta är ett misstag (t.ex. att en legitim workflow-fråga felklassificerats), använd "Överklaga"-länken nedan så går vi igenom incidenten manuellt.',
                };
              }
              return updated;
            });
          }
        }
        // Strip the violation tag from the displayed message
        setMessages((prev) =>
          prev.map((m) => ({
            ...m,
            content: m.content.replace(/\[SCOPE_VIOLATION\]\s*/g, ''),
          })),
        );
      }

      // Persist:era assistant-svaret i conversation (Batch CCC).
      // Failar silent om ingen conversation finns (t.ex. signed-out test).
      // Strippa JSON-fences — råa workflow-JSON-blobbar ska inte ligga i
      // chat_message.content (uppblåst historik + exponerar schema när
      // user laddar gamla konversationer). Version-historiken ägs av
      // artifact_version via I-001:s wire-up.
      if (conversationIdRef.current && assistantSoFar) {
        const persistContent = stripJsonBlocks(
          assistantSoFar.replace(/\[SCOPE_VIOLATION\]\s*/g, ''),
        );
        if (persistContent) {
          void appendMessage({
            conversationId: conversationIdRef.current,
            role: 'assistant',
            content: persistContent,
          });
        }
      }

      // A-113: Layout-validator (admin-only). Om checkrutan är aktiv och
      // AI:n levererat workflow-JSON kör vi geometri-reglerna. Errors → kasta
      // tillbaka feedback som ny user-message så AI:n regenererar. Cap:as
      // till LAYOUT_MAX_RETRIES retries per send för att undvika loopar.
      if (validateLayoutEnabled && assistantSoFar) {
        const jsonStrings = extractJsonBlocks(assistantSoFar);
        if (jsonStrings.length > 0) {
          try {
            let parsed = JSON.parse(jsonStrings[0]);
            parsed = normaliseLeanFormat(parsed);
            if (isCompactFormat(parsed)) parsed = expandCompactWorkflow(parsed);
            const state = parseWorkflowState(parsed);
            if (state) {
              const result = validateLayout(state as unknown as WorkflowState);
              if (result.ok && result.warningCount === 0) {
                toast({
                  title: 'Layout-validering OK',
                  description: 'Inga regelbrott upptäcktes.',
                });
                layoutRetryCountRef.current = 0;
              } else if (
                result.errorCount > 0 &&
                layoutRetryCountRef.current < LAYOUT_MAX_RETRIES
              ) {
                layoutRetryCountRef.current += 1;
                const feedback = formatViolationsForAi(result);
                toast({
                  title: `Layout-validering (försök ${layoutRetryCountRef.current}/${LAYOUT_MAX_RETRIES})`,
                  description: `${result.errorCount} error(s), ${result.warningCount} warning(s). Skickar tillbaka till AI:n.`,
                });
                const feedbackMsg: Msg = { role: 'user', content: feedback };
                setMessages((prev) => [...prev, feedbackMsg]);
                const nextMessages: Msg[] = [
                  ...allMessages,
                  { role: 'assistant', content: assistantSoFar },
                  feedbackMsg,
                ];
                // Fire-and-forget retry — current send avslutas normalt.
                void sendMessagesRef.current(nextMessages);
              } else if (result.errorCount > 0) {
                toast({
                  title: 'Layout-validering: max retries nådd',
                  description: `${result.errorCount} error(s) kvarstår efter ${LAYOUT_MAX_RETRIES} försök. Inspektera JSON manuellt.`,
                  variant: 'destructive',
                });
                layoutRetryCountRef.current = 0;
              } else {
                toast({
                  title: 'Layout-validering: bara warnings',
                  description: `${result.warningCount} warning(s) — accepterad.`,
                });
                layoutRetryCountRef.current = 0;
              }
            }
          } catch {
            // JSON-parse fail — annan validering hanterar det
          }
        }
      }
    } catch (e: unknown) {
      if ((e as { name?: string })?.name === 'AbortError') {
        // Superseded by a newer send — don't touch `isLoading`; the
        // replacement call owns it now.
        return;
      }
      // Attach the error as an inline badge on the last assistant
      // message (or append a new error-only message) instead of
      // blasting a destructive toast over the chat. Toast is reserved
      // for configuration problems the user must resolve elsewhere.
      const errMessage = e instanceof Error ? e.message : String(e);
      setMessages(prev => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
          updated[lastIdx] = { ...updated[lastIdx], error: errMessage };
        } else {
          updated.push({ role: 'assistant', content: '', error: errMessage });
        }
        return updated;
      });
    } finally {
      // Only reset if this send is still the active one.
      if (chatAbortRef.current === ourController) {
        setIsLoading(false);
      }
    }
  }, [config, getChatUrl, moderationV2, dbStrikes, appendMessage, validateLayoutEnabled, conversationIdRef, persistConfig, aiHoningPromptRef, setActiveFlag, setIsLoading, setLegacyBanned, setLegacyStrikes, setMessages, setPendingToolCall]);
  useEffect(() => { sendMessagesRef.current = sendMessages; }, [sendMessages]);

  return {
    sendMessages,
    sendMessagesRef,
    chatAbortRef,
    layoutRetryCountRef,
  };
}
